"use client";

import { useImpersonation } from "@/hooks/useImpersonation";
import { ImpersonationRole, ImpersonationMemberType } from "@/lib/auth/ImpersonationTypes";
import { AlertTriangle } from "lucide-react";

interface StopImpersonationButtonProps {
  effectiveRole: ImpersonationRole;
  impersonationInfo?: {
    isImpersonating: boolean;
    actualMemberType?: ImpersonationMemberType | null;
    effectiveMemberType?: ImpersonationMemberType | null;
  };
}

export default function StopImpersonationButton({ effectiveRole, impersonationInfo }: StopImpersonationButtonProps) {
  const { loading, stopImpersonation } = useImpersonation();

  const handleStopImpersonation = async () => {
    const success = await stopImpersonation();
    if (success) {
      // Always navigate to root page to avoid permission issues
      window.location.href = '/?t=' + Date.now();
    }
  };

  const getRoleLabel = (role: ImpersonationRole) => {
    switch (role) {
      case "admin":
        return "Admin";
      case "member":
        // If we have member type info, show the specific type
        if (impersonationInfo?.effectiveMemberType) {
          switch (impersonationInfo.effectiveMemberType) {
            case "owner":
              return "Owner";
            case "board-member":
              return "Board Member";
            case "renter":
              return "Renter";
            case "property-manager":
              return "Property Manager";
            case "portal-admin":
              return "Portal Admin";
            default:
              return "Member";
          }
        }
        return "Member";
      case "public":
        return "Public";
    }
  };

  return (
    <button
      onClick={handleStopImpersonation}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <AlertTriangle className="h-4 w-4" />
      Stop Viewing as {getRoleLabel(effectiveRole)}
      {loading && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
      )}
    </button>
  );
} 